import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { TreeView } from "@mui/x-tree-view/TreeView";

import { useDebounce } from "hooks";

import { neutral } from "app/themes/colors";
import { PA_DIALOG_YIELD_PAINT_V1 } from "constants/feature-flags";
import { useGetCommsTemplateCategoriesQuery } from "services/suno-comms/suno-comms-api";
import { TemplateSourceType } from "services/suno-comms/types/text-templates.type";
import { useGetTemplateCategoriesQuery } from "services/template-categories";
import { useLazyGetTextTemplateQuery } from "services/text-templates";
import { TemplateCategory } from "services/types/template-categories.type";
import { TextTemplate } from "services/types/text-templates.type";

import {
  mapPatientMergeTagsForLeads,
  mapPatientMergeTagsInDesignJson
} from "./cross-platform-merge-tags.util";
import TextTemplatesTreeItems from "./text-templates-tree-items.component";
import {
  TreeNode,
  getTemplateSource,
  getTemplatesFlatNodes,
  getTemplatesNodesTree,
  makeGroupNode
} from "./text-templates-v2.utils";
import { useMergedTemplatePicker } from "./use-merged-template-picker.hook";

// Stable fallback for category queries that are skipped or not yet loaded. An inline `= []`
// default mints a new array identity on every render, which re-triggers the tree-build effect
// below and locks the picker in a setExpanded render loop that swallows every manual
// expand/collapse (practice 178 report: subcategories like JF could never be opened).
const NO_CATEGORIES: TemplateCategory[] = [];

const TEAL = "#0F6E56";
const TEAL_FILL = "#E1F5EE";
const TEAL_BORDER = "#9FE1CB";

const arraysEqual = (a: unknown[], b: unknown[]): boolean => {
  return a.length === b.length && a.every((val, index) => val === b[index]);
};

type SelectTemplateFieldProps = {
  templateTypes?: number[];
  search: string;
  selected?: string;
  expanded: string[];
  composeTemplateEnabled?: boolean;
  templateSource?: TemplateSourceType;
  onSelect: (template: TextTemplate | null) => void;
  onExpand: (nodeIds: string[]) => void;
  setSearch: (value: string) => void;
  onAddToComposedTemplate?: (text: string) => void;
};

export interface SelectTemplateFieldRef {
  focusSearchField: () => void;
}

const SelectTemplateFieldInner = React.forwardRef<
  SelectTemplateFieldRef,
  SelectTemplateFieldProps
>(
  (
    {
      templateTypes,
      search,
      selected,
      expanded,
      onSelect,
      onExpand,
      setSearch,
      composeTemplateEnabled,
      templateSource = "default",
      onAddToComposedTemplate
    },
    ref
  ) => {
    const isLeads = templateSource === "leads";
    const flags = useFlags();
    const yieldToPaintEnabled = flags[PA_DIALOG_YIELD_PAINT_V1];
    const { value: dSearch } = useDebounce(search, 250);
    const [nodes, setNodes] = useState<TreeNode[]>([]);
    const [pendingNodeId, setPendingNodeId] = useState<string | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        focusSearchField: () => {
          if (searchInputRef.current) {
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }

            requestAnimationFrame(() => {
              if (searchInputRef.current) {
                searchInputRef.current.focus();
                searchInputRef.current.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest"
                });
              }
            });
          }
        }
      }),
      []
    );

    // Main-app categories are always read (Leads surfaces merge main-app
    // templates in, so their categories are needed too). Comms categories load
    // only on Leads surfaces.
    const {
      data: defaultCategories = NO_CATEGORIES,
      isFetching: isDefaultCategoriesLoading
    } = useGetTemplateCategoriesQuery({ is_active: true });
    const {
      data: commsCategories = NO_CATEGORIES,
      isFetching: isCommsCategoriesLoading
    } = useGetCommsTemplateCategoriesQuery(
      { is_active: true },
      { skip: !isLeads }
    );
    const categories: TemplateCategory[] = isLeads
      ? commsCategories
      : defaultCategories;
    const isCategoriesLoading = isLeads
      ? isCommsCategoriesLoading || isDefaultCategoriesLoading
      : isDefaultCategoriesLoading;

    // Search, type filtering, and pagination all happen server-side so practices
    // with more templates than the backend's 300-row cap can still find and
    // browse every template (ENG-1426).
    const {
      templates,
      totalCount,
      hasMore,
      isLoading: isTemplatesLoading,
      isLoadingMore,
      loadMore
    } = useMergedTemplatePicker({
      templateTypes,
      search: dSearch,
      templateSource
    });

    const [fetchTemplateText] = useLazyGetTextTemplateQuery();

    const hasAutoExpandedRef = useRef(false);
    useEffect(() => {
      hasAutoExpandedRef.current = false;
    }, [dSearch]);

    useEffect(() => {
      if (!isCategoriesLoading && !isTemplatesLoading) {
        const isSearching = !!dSearch.trim();
        let result: TreeNode[];
        if (isSearching) {
          // While searching, show a flat list so every server-matched template
          // (from either backend) is visible instead of buried in a group.
          result = getTemplatesFlatNodes(templates);
        } else if (isLeads) {
          // Leads surfaces merge two stores: present them as two labeled groups
          // ("Suno Leads" / "Main App"), each with its own category subtree.
          // Distinct `noCategoryId`s keep the two "No Category" folders from
          // colliding on selector `c-1` and breaking TreeView expansion.
          const leadsTemplates = templates.filter(
            (template) => getTemplateSource(template) === "leads"
          );
          const defaultTemplates = templates.filter(
            (template) => getTemplateSource(template) === "default"
          );
          const leadsSubtree = getTemplatesNodesTree(
            commsCategories,
            leadsTemplates,
            "",
            -1
          );
          const defaultSubtree = getTemplatesNodesTree(
            defaultCategories,
            defaultTemplates,
            "",
            -2
          );
          result = [
            leadsSubtree.length
              ? makeGroupNode("group-leads", "Leads Templates", leadsSubtree)
              : null,
            defaultSubtree.length
              ? makeGroupNode(
                  "group-default",
                  "Clinic Templates",
                  defaultSubtree
                )
              : null
          ].filter((node): node is TreeNode => node !== null);
        } else {
          result = getTemplatesNodesTree(categories, templates, "");
        }

        // Auto-expand a small root set only once per search state. Re-running this on every
        // effect pass would overwrite the user's manual expansion (each onExpand call resets
        // the controlled `expanded` array to just the roots).
        if (
          !isSearching &&
          result.length &&
          result.length <= 3 &&
          !hasAutoExpandedRef.current
        ) {
          hasAutoExpandedRef.current = true;
          onExpand(result.map((node) => node.selector));
        }
        setNodes(result);
      }
    }, [
      dSearch,
      isLeads,
      categories,
      commsCategories,
      defaultCategories,
      templates,
      isCategoriesLoading,
      isTemplatesLoading,
      onExpand
    ]);

    const handleAddToComposedTemplate = useCallback(
      async (templateId: number | string) => {
        const template = templates.find(
          (item) => String(item.id) === String(templateId)
        );
        if (!template) {
          onAddToComposedTemplate?.("");
          return;
        }
        // Leads (Comms) rows already carry full `text`; main-app rows project it
        // out, so only that path needs a detail fetch from the main backend.
        if (getTemplateSource(template) === "leads") {
          onAddToComposedTemplate?.(template.text ?? "");
          return;
        }
        const result = await fetchTemplateText({
          id: templateId,
          query: "{text}"
        });
        const text = result.data?.text ?? "";
        onAddToComposedTemplate?.(
          isLeads ? mapPatientMergeTagsForLeads(text) : text
        );
      },
      [isLeads, templates, onAddToComposedTemplate, fetchTemplateText]
    );

    const handleNodeToggle = useCallback(
      (event: any, nodeIds: string[] | string) => {
        if (Array.isArray(nodeIds)) {
          onExpand(nodeIds);
        } else {
          onExpand([nodeIds]);
        }
      },
      [onExpand]
    );

    const handleNodeSelect = useCallback(
      async (event: any, nodeId: string) => {
        if (yieldToPaintEnabled) {
          if (pendingNodeId) {
            return;
          }
          // Only template nodes ("t<id>") fetch async; folder selection is
          // synchronous and needs no optimistic highlight or spinner.
          if (nodeId.startsWith("t")) {
            setPendingNodeId(nodeId);
          }
        }
        try {
          if (nodeId.startsWith("t")) {
            // The selector keeps the raw id (UUID or bigint), so match as a
            // string instead of coercing — `Number(uuid)` would be NaN.
            const rawId = nodeId.slice(1);
            const template = templates.find(
              (template) => String(template.id) === rawId
            );
            if (!template) {
              onSelect(null);
            } else if (getTemplateSource(template) === "leads") {
              // Leads (Comms) rows already include full text/subject/design.
              onSelect(template);
            } else {
              const result = await fetchTemplateText({
                id: template.id,
                query: "{text,subject_template,email_design_json}"
              });
              const text = result.data?.text ?? "";
              const subjectTemplate = result.data?.subject_template;
              const emailDesignJson = result.data?.email_design_json;
              onSelect({
                ...template,
                text: isLeads ? mapPatientMergeTagsForLeads(text) : text,
                subject_template:
                  isLeads && subjectTemplate
                    ? mapPatientMergeTagsForLeads(subjectTemplate)
                    : subjectTemplate,
                email_design_json:
                  isLeads && emailDesignJson
                    ? mapPatientMergeTagsInDesignJson(emailDesignJson)
                    : emailDesignJson
              });
            }
          } else {
            onSelect(null);
          }
        } finally {
          if (yieldToPaintEnabled) {
            setPendingNodeId(null);
          }
        }
      },
      [
        isLeads,
        onSelect,
        templates,
        fetchTemplateText,
        yieldToPaintEnabled,
        pendingNodeId
      ]
    );

    const handleSearchChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
      },
      [setSearch]
    );

    const effectiveSelected = yieldToPaintEnabled
      ? (pendingNodeId ?? selected)
      : selected;

    return (
      <Stack
        sx={{
          width: 340,
          flexShrink: 0,
          height: "100%",
          background: "#fff",
          border: `1px solid ${neutral[200]}`,
          borderRadius: "10px",
          p: 2
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          columnGap={0.75}
          sx={{ mb: 1.5, flexShrink: 0 }}
        >
          <MenuBookOutlinedIcon sx={{ fontSize: 16, color: neutral[600] }} />
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: neutral[600],
              lineHeight: 1.4
            }}
          >
            Template library
          </Typography>
        </Stack>
        <TextField
          inputRef={searchInputRef}
          value={search}
          fullWidth
          size="small"
          placeholder="Search templates"
          sx={{
            mb: 1.5,
            flexShrink: 0,
            "& .MuiOutlinedInput-root": {
              backgroundColor: neutral[50],
              borderRadius: "8px"
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: neutral[200]
            }
          }}
          onChange={handleSearchChange}
        />
        {yieldToPaintEnabled && pendingNodeId && (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ pl: 1, pb: 1 }}
          >
            <CircularProgress size={14} />
            <Typography variant="caption">Loading template…</Typography>
          </Stack>
        )}
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pr: 0.5 }}>
          {isTemplatesLoading ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ flex: 1, py: 4 }}
            >
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <>
              <TreeView
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
                expanded={expanded}
                selected={effectiveSelected}
                onNodeToggle={handleNodeToggle}
                onNodeSelect={handleNodeSelect}
                sx={{
                  "& .MuiTreeItem-content": {
                    borderRadius: "8px",
                    border: "1px solid transparent"
                  },
                  "& .MuiTreeItem-content:hover": {
                    backgroundColor: neutral[50]
                  },
                  "& .MuiTreeItem-content.Mui-selected, & .MuiTreeItem-content.Mui-selected.Mui-focused, & .MuiTreeItem-content.Mui-selected:hover":
                    {
                      backgroundColor: TEAL_FILL,
                      border: `1px solid ${TEAL_BORDER}`,
                      color: TEAL
                    }
                }}
              >
                <TextTemplatesTreeItems
                  nodes={nodes}
                  composeTemplateEnabled={composeTemplateEnabled}
                  onAddToComposedTemplate={handleAddToComposedTemplate}
                />
              </TreeView>
              {!!totalCount && (
                <Stack spacing={0.5} sx={{ mt: 1, alignItems: "center" }}>
                  <Typography variant="caption" color="text.secondary">
                    Showing {templates.length} of {totalCount}
                  </Typography>
                  {hasMore && (
                    <Button
                      size="small"
                      onClick={loadMore}
                      disabled={isLoadingMore}
                    >
                      {isLoadingMore ? "Loading…" : "Show more"}
                    </Button>
                  )}
                </Stack>
              )}
            </>
          )}
        </Box>
      </Stack>
    );
  }
);

SelectTemplateFieldInner.displayName = "SelectTemplateFieldInner";

const SelectTemplateField = React.memo(
  SelectTemplateFieldInner,
  (prevProps, nextProps) => {
    // Custom comparison to optimize re-renders
    return (
      prevProps.search === nextProps.search &&
      prevProps.selected === nextProps.selected &&
      prevProps.composeTemplateEnabled === nextProps.composeTemplateEnabled &&
      prevProps.templateSource === nextProps.templateSource &&
      arraysEqual(prevProps.expanded || [], nextProps.expanded || []) &&
      arraysEqual(
        prevProps.templateTypes || [],
        nextProps.templateTypes || []
      ) &&
      prevProps.onSelect === nextProps.onSelect &&
      prevProps.onExpand === nextProps.onExpand &&
      prevProps.setSearch === nextProps.setSearch &&
      prevProps.onAddToComposedTemplate === nextProps.onAddToComposedTemplate
    );
  }
);

SelectTemplateField.displayName = "SelectTemplateField";

export default SelectTemplateField;
